import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Eye, ArrowRight, Search } from 'lucide-react'
import { Card, CardContent } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { SiteFooter } from '../components/SiteFooter'
import { getImageUrl } from '../lib/catalogUrl'

const API_URL = import.meta.env.VITE_API_URL || '/api'

type CatalogEntry = {
  id: number
  name: string
  slug: string
  logoUrl?: string | null
  description?: string | null
  viewCount?: number
  catalogUrl: string
  badge?: string | null
  plan?: string
  searchPriority?: number
}

export default function CatalogDirectory() {
  const [q, setQ] = useState('')

  useEffect(() => {
    document.title = 'Direktori Katalog UMKM | UMKM Indonesia'
    const desc = 'Jelajahi katalog produk UMKM terbaru dan populer. Setiap usaha memiliki halaman katalog unik di UMKM Indonesia.'
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'description'
      document.head.appendChild(meta)
    }
    meta.content = desc

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = `${window.location.origin}/catalog`
  }, [])

  const { data: catalogs = [], isLoading } = useQuery<CatalogEntry[]>({
    queryKey: ['brands', 'public', 'catalogs'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/brands/public/catalogs`)
      if (!res.ok) throw new Error('Gagal memuat katalog')
      return res.json()
    },
  })

  const filtered = catalogs.filter(
    (c) =>
      c.name.toLowerCase().includes(q.toLowerCase()) ||
      c.slug.toLowerCase().includes(q.toLowerCase())
  )

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full">
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Direktori Katalog UMKM</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Temukan katalog produk dari pelaku UMKM. Setiap halaman katalog unik dan dapat diindeks oleh Google.
        </p>
      </div>

      <div className="max-w-md mx-auto mb-8 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cari nama usaha..."
          className="pl-10"
        />
      </div>

      {isLoading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-gray-500 py-16">Belum ada katalog yang tersedia.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((catalog) => {
            const catalogHref = catalog.catalogUrl || `/catalog/${catalog.slug}`

            return (
              <Card key={catalog.slug} className="hover:shadow-md transition-shadow group">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center shrink-0">
                      {catalog.logoUrl ? (
                        <img
                          src={getImageUrl(catalog.logoUrl) || ''}
                          alt={catalog.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-white font-bold text-lg w-full h-full flex items-center justify-center bg-indigo-500">
                          {catalog.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                          {catalog.name}
                        </h2>
                        {catalog.badge && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
                            catalog.badge === 'Premium' ? 'bg-amber-500 text-white' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {catalog.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">/catalog/{catalog.slug}</p>
                      {catalog.description && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{catalog.description}</p>
                      )}
                      {catalog.viewCount != null && catalog.viewCount > 0 && (
                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                          <Eye className="w-3 h-3" />
                          <span>{catalog.viewCount.toLocaleString()} views</span>
                        </div>
                      )}
                      <a
                        href={catalogHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                      >
                        Buka katalog <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      <p className="text-center text-sm text-gray-500 mt-10 flex items-center justify-center gap-1">
        <Eye className="h-4 w-4" />
        {filtered.length} katalog tersedia untuk dijelajahi
      </p>

      {!isLoading && filtered.length > 0 && (
        <nav className="sr-only" aria-label="Daftar link katalog">
          <ul>
            {filtered.map((catalog) => (
              <li key={catalog.slug}>
                <a href={catalog.catalogUrl || `/catalog/${catalog.slug}`}>{catalog.name}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}
      </div>

      <SiteFooter />
    </div>
  )
}
